import { useEffect, useMemo, useRef } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';

import {
  selectSelectedAppBuild,
  setSelectedAppBuild,
  selectSelectedDevice,
} from '../../redux/app/platformSlice';
import {
  getBuildVersionsForFilters,
  selectBuildsFilters,
  selectLoadingFilters,
} from '../../redux/runSummary/runSummarySlice';

export function SelectAppBuildForAppiumAutomation() {
  const selectedAppBuild = useSelector(selectSelectedAppBuild);
  const selectedDevice = useSelector(selectSelectedDevice);
  const filtersVersion = useSelector(selectBuildsFilters);
  const loadingBuilds = useSelector(selectLoadingFilters);

  const dispatch = useDispatch();
  const selectedDeviceType = selectedDevice?.type;

  // Fetch build versions lazily — only when the user opens the dropdown, and only
  // once (the result covers both platforms and is cached in Redux for the session).
  const buildsFetchedRef = useRef(false);
  const handleOpen = () => {
    if (!selectedDeviceType || buildsFetchedRef.current) return;
    buildsFetchedRef.current = true;
    const hasBuilds =
      (filtersVersion.ios?.length ?? 0) > 0 || (filtersVersion.android?.length ?? 0) > 0;
    if (!hasBuilds) {
      dispatch(getBuildVersionsForFilters());
    }
  };

  const IOS_APP_BUILD_OPTIONS = useMemo(
    () =>
      (filtersVersion.ios ?? []).map((version) => ({
        label: version,
        value: version,
      })),
    [filtersVersion.ios],
  );

  const ANDROID_APP_BUILD_OPTIONS = useMemo(
    () =>
      (filtersVersion.android ?? []).map((version) => ({
        label: version,
        value: version,
      })),
    [filtersVersion.android],
  );

  const prevDeviceTypeRef = useRef(selectedDeviceType);
  useEffect(() => {
    if (prevDeviceTypeRef.current !== selectedDeviceType) {
      prevDeviceTypeRef.current = selectedDeviceType;
      dispatch(setSelectedAppBuild(''));
    }
  }, [selectedDeviceType, dispatch]);

  const APP_BUILD_OPTIONS = useMemo(() => {
    if (!selectedDeviceType)
      return [{ label: 'Please select a device', value: '__no_device__', disabled: true }];

    return selectedDeviceType === 'android' ? ANDROID_APP_BUILD_OPTIONS : IOS_APP_BUILD_OPTIONS;
  }, [selectedDeviceType, IOS_APP_BUILD_OPTIONS, ANDROID_APP_BUILD_OPTIONS]);

  return (
    <Autocomplete
      freeSolo
      size="small"
      options={APP_BUILD_OPTIONS}
      onOpen={handleOpen}
      loading={loadingBuilds}
      loadingText="Loading builds…"
      getOptionDisabled={(option) => option?.disabled === true}
      value={
        APP_BUILD_OPTIONS?.find((option) => option.value === selectedAppBuild) || selectedAppBuild
      }
      onChange={(_, newValue) => {
        dispatch(setSelectedAppBuild(newValue?.value || ''));
      }}
      onInputChange={(_, newInput, reason) => {
        if (reason === 'input') {
          dispatch(setSelectedAppBuild(newInput));
        }
      }}
      sx={{
        minWidth: 200,
        bgcolor: 'background.paper',
        borderRadius: 2,
        boxShadow: 1,
        '& .MuiOutlinedInput-root': {
          '&:hover fieldset': { borderColor: 'text.secondary' },
          '&.Mui-focused fieldset': { borderColor: 'primary.main' },
        },
        '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="App Build (Required)"
          inputProps={{
            ...params.inputProps,
            readOnly: !selectedDeviceType,
          }}
        />
      )}
    />
  );
}
